import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import useBackdrop from "@/hooks/use-backdrop";
import { useSocket } from "@/hooks/use-socket";
import { tfaAtom } from "@/lib/atoms";
import { getSiteToken, useApi } from "@/lib/pariette";
import { City, Country, LookupItem, Store, TableData, User } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useSetAtom } from "jotai";
import { AlertCircleIcon, Check, ChevronDown, Copy, Loader2, Plus, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import Autocomplete from "./Autocomplete";
import CreateUser from "./CreateUser";

interface Props {
  title?: string;
  openModal: boolean;
  closeModal: () => void;
  applicationForm?: Partial<Form>;
}

type LookupList = {
  printerTypes: LookupItem[];
  storeTypes: LookupItem[];
  periodTypes: LookupItem[];
};

type Form = {
  company: string | null;
  site: string | undefined;
  title: string;
  user_name: string;
  mwst_office: string;
  mwst_number: string;
  country: string;
  city: string;
  postal_code: string;
  address_1: string;
  address_2: string;
  tel1_title: string;
  tel1: string;
  email1_title: string;
  email1: string;
  store_card: string | null;
  type: number;
  balance: number;
  credit: number;
  credit_limit: number;
  inv_period: number | null;
  printer: number | null;
  status: number;
  user_token: string;
  note: string;
  geo_x: string;
  geo_y: string;
  paysafe_api: string;
  paysafe_limit: string;
  store_application_id?: number;
  representative: string;
};

type SiteUser = {
  company: string;
  site: string;
  token: string;
  name: string;
  email: string;
  reset_method: string;
};

const defaultForm: Form = {
  company: null,
  site: undefined,
  title: "",
  user_name: "",
  mwst_office: "",
  mwst_number: "",
  country: "",
  city: "",
  postal_code: "",
  address_1: "",
  address_2: "",
  tel1_title: "",
  tel1: "",
  email1_title: "",
  email1: "",
  store_card: null,
  type: 101,
  balance: 0,
  credit: 0,
  credit_limit: 0,
  inv_period: null,
  printer: null,
  status: 1,
  user_token: "",
  note: "",
  geo_x: "",
  geo_y: "",
  paysafe_api: "",
  paysafe_limit: "",
  representative: "",
};

const rulesData = {
  operation: "create",
};

const stringVal = (value: unknown) => !!(value && typeof value === "string" && value.length > 0);
const numberVal = (value: unknown) => !!(value && typeof value === "number" && !isNaN(value) && value > 0);

const validations: Record<keyof Form, (value: Form[keyof Form]) => boolean> = {
  title: stringVal,
  country: stringVal,
  city: stringVal,
  address_1: stringVal,
  email1_title: stringVal,
  email1: stringVal,
  type: numberVal,
  inv_period: numberVal,
  printer: numberVal,
  user_name: stringVal,
  user_token: (value) => true, //copyalamada problem var
  mwst_office: (value) => true,
  mwst_number: (value) => true,
  tel1_title: (value) => true,
  tel1: (value) => true,
  postal_code: (value) => true,
  address_2: (value) => true,
  credit: (value) => true,
  credit_limit: (value) => true,
  status: (value) => true,
  note: (value) => true,
  geo_x: (value) => true,
  geo_y: (value) => true,
  store_card: (value) => true,
  balance: (value) => true,
  company: (value) => true,
  site: (value) => true,
  paysafe_api: (value) => true,
  paysafe_limit: (value) => true,
  store_application_id: (value) => true,
  representative: (value) => true,
};

const checkField = (field: keyof Form, form: Form) => validations[field](form[field]);

function ParietteCreateModal({ title, openModal, closeModal, applicationForm }: Props) {
  const t = useTranslations("general");
  const [expanded, setExpanded] = useState<string>(applicationForm ? "panel2" : "panel1");
  const [loader, setLoader] = useState(false);
  const [isStoreCardReadonly, setReadonly] = useState(false);
  const [form, setForm] = useState<Form>(applicationForm ? { ...defaultForm, ...applicationForm } : defaultForm);
  const setIs2faModal = useSetAtom(tfaAtom);
  const { checkRules } = useSocket();

  const create = () => {
    setForm(defaultForm);
    setReadonly(false);
    setExpanded("panel2");
  };

  const copyItem = (s: Store) => {
    setReadonly(true);
    setForm((prev) => {
      return {
        ...prev,
        store_card: s.token,
        title: s.title,
        user_name: s.user_name,
        mwst_office: s.mwst_office ?? "",
        mwst_number: s.mwst_number,
        country: s.country,
        city: s.city,
        postal_code: s.postal_code ?? "",
        address_1: s.address_1 ?? "",
        address_2: s.address_2 ?? "",
        tel1_title: s.tel1_title,
        tel1: s.tel1,
        email1_title: s.email1_title,
        email1: s.email1,
        paysafe_api: "",
        paysafe_limit: "",
      };
    });

    setExpanded("panel2");
  };

  const submitForm = async () => {
    const checkForm = Object.entries(validations).every(([key, value]) => checkField(key as keyof Form, form));
    if (!checkForm) {
      const message = (
        <>
          <p className="text-lg">{t("fillError")}</p>
          {Object.entries(form)
            .map(([key, value]) => (checkField(key as keyof Form, form) ? "" : t(key)))
            .filter((v) => v.length > 0)
            .join(" ,  ")}
        </>
      );
      console.log(form);
      toast.error(message, { position: "top-center", richColors: true });
      return;
    }
    if (applicationForm) {
      form.store_application_id = applicationForm?.store_application_id ?? undefined;
    }
    setIs2faModal(true);
    setLoader(true);

    await checkRules(rulesData, ["Store Creation Process."], { api: "widget/store-card-user", data: form }, (res: any) => afterCheck(res));
  };

  const afterCheck = async (res: any) => {
    setIs2faModal(false);
    setLoader(false);
    if (res.status) {
      toast.success(res.message, { richColors: true, position: "top-center", className: "!text-2xl" });
      closeModal();
    } else {
    }
  };

  return (
    <>
      <Dialog
        open={openModal}
        onOpenChange={(o) => {
          if (!o) {
            closeModal();
          }
        }}
      >
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="min-w-[60%] min-h-[600px] flex flex-col overflow-visible">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1">
            <Accordion value={expanded} type="single" collapsible onValueChange={setExpanded}>
              <AccordionItem value="panel1" disabled={!!applicationForm}>
                <AccordionTrigger className="border-t border-x rounded-md px-2">
                  <p>{t("searchStoreCard")}</p>
                </AccordionTrigger>
                <SearchStage onCopy={copyItem} onCreate={create} />
              </AccordionItem>
              <AccordionItem value="panel2">
                <AccordionTrigger className="border-t border-x px-2">
                  <p>{t("create_store_step1")}</p>
                </AccordionTrigger>
                <StoreDetailStage isStoreCardReadonly={isStoreCardReadonly} loader={loader} form={form} setForm={setForm} isOpen={expanded === "panel2"} applicationForm={applicationForm} />
              </AccordionItem>
              <AccordionItem value="panel3" className="border-b-0">
                <AccordionTrigger className="border-t border-x px-2">
                  <p>{form.title ? t("create_store_step2") + ": " + form.title : t("create_store_step2")}</p>
                </AccordionTrigger>
                <FinalStage isStoreCardReadonly={isStoreCardReadonly} loader={loader} form={form} setForm={setForm} isOpen={expanded === "panel3"} />
              </AccordionItem>
            </Accordion>
          </div>
          <DialogFooter className="flex items-end justify-end gap-2">
            <Button onClick={() => closeModal()} variant={"destructive"} size={"lg"} loading={loader}>
              <X />
              {t("cancel")}
            </Button>
            <Button variant="success" onClick={submitForm} loading={loader} size={"lg"}>
              <Check />
              {t("createStore")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const SearchStage = ({ onCreate, onCopy }: { onCreate: () => void; onCopy: (s: any) => void }) => {
  const t = useTranslations("general");
  const { $get } = useApi();
  const [tableData, setTableData] = useState<TableData<any>>();
  const [search, setSearch] = useState("");
  const [loader, setLoader] = useState(false);

  const searchStore = async (searchText: string) => {
    if (searchText.length >= 3) {
      const searchq: Record<string, string> = {
        user_name: searchText,
        title: searchText,
        address_1: searchText,
        mwst_number: searchText,
        all: "false",
      };
      const s = new URLSearchParams(searchq).toString();

      setLoader(true);
      await $get(`store-card?${s}`)
        .then((res) => {
          if (Object.entries(res.data).length == 0) {
            setTableData(undefined);
          } else {
            setTableData(res);
          }
        })
        .catch((err) => {
          setTableData(undefined);
          //error yazdır
        })
        .finally(() => {
          setLoader(false);
        });
    } else {
      setTableData(undefined);
    }
  };

  const handleSearch = async (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      searchStore(search);
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <AccordionContent className="max-h-[40dvh] overflow-y-auto p-4 border-b border-x">
      <div className="flex flex-col md:flex-row items-end gap-2">
        <div className="flex-1">
          <Label>{t("search")}</Label>
          <Input id="search-card" placeholder={t("autocompleteDesc")} value={search} onChange={handleSearch} />
        </div>
        <Button variant="success" onClick={onCreate} className="h-10">
          <Plus />
          {t("orCreateNewStore")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("searchAutocomplete")}</p>
      {loader ? (
        <div className="flex flex-col items-center justify-center min-h-[500px]">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <Table className="border rounded-md mt-4">
          <TableHeader>
            <TableRow>
              <TableHead>{t("title")}</TableHead>
              <TableHead>{t("user")}</TableHead>
              <TableHead>{t("address")}</TableHead>
              <TableHead>{t("copy")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableData?.data?.slice(0, 10).map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  {row.code} {row.title}
                </TableCell>
                <TableCell>
                  {row.user_name}
                  <p className="text-xs text-muted-foreground">{row.email1}</p>
                </TableCell>
                <TableCell>
                  <address className="text-xs">
                    {row.address_1} {row.postal_code}
                    <br />
                    {row.city_name} / {row.country_name}
                  </address>
                </TableCell>
                <TableCell align="center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button onClick={() => onCopy(row)} variant={"ghost"} size={"icon"}>
                        <Copy />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("copy")}</TooltipContent>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </AccordionContent>
  );
};

const StoreDetailStage = ({
  isStoreCardReadonly,
  loader,
  form,
  setForm,
  isOpen,
  applicationForm,
}: {
  isStoreCardReadonly: boolean;
  loader: boolean;
  form: Form;
  setForm: Dispatch<SetStateAction<Form>>;
  isOpen: boolean;
  applicationForm?: Partial<Form>;
}) => {
  const t = useTranslations("general");
  const { $get } = useApi();
  const [cities, setCities] = useState<City[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);

  const fetchCity = async (m: string) => {
    await $get(`city?country=${m}`).then((res) => {
      setCities(res.data);
    });
  };

  const getCountries = async () => {
    await $get("country").then((res) => {
      setCountries(res.data);
      if (applicationForm?.country) {
        const country = res.data.find((item: Country) => item.id.toString() === applicationForm.country?.toString());
        if (country?.name) {
          fetchCity(country.name);
        }
      }
    });
  };

  useEffect(() => {
    if (isOpen && countries.length === 0) {
      getCountries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <AccordionContent className="max-h-[40dvh] overflow-y-auto p-4 border-b border-x">
      {loader ? (
        <div className="flex flex-col items-center justify-center min-h-[500px]">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <div className="grid-cols-4 grid sm:grid-cols-8 md:grid-cols-12 gap-2">
          <div className={cn("col-span-4", isStoreCardReadonly ? "col-span-4 md:col-span-6" : "col-span-8 md:col-span-12")}>
            <Label error={!checkField("title", form)} htmlFor={t("title")}>
              {t("title")}
            </Label>
            <Input
              id={t("title")}
              disabled={loader}
              type="text"
              error={!checkField("title", form)}
              value={form.title}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, title: e.target.value };
                })
              }
            />
          </div>
          {isStoreCardReadonly && (
            <div className="col-span-4 md:col-span-6">
              <Label error={!checkField("user_name", form)} htmlFor={t("user_name")}>
                {t("user_name")}
              </Label>
              <Input
                id={t("user_name")}
                disabled={loader}
                type="text"
                error={!checkField("user_name", form)}
                value={form.user_name}
                onChange={(e) =>
                  setForm((prev) => {
                    return { ...prev, user_name: e.target.value };
                  })
                }
              />
            </div>
          )}

          <div className="col-span-4 md:col-span-6">
            <Label error={!checkField("mwst_office", form)} htmlFor={t("mwst_office")}>
              {t("mwst_office")}
            </Label>
            <Input
              id={t("mwst_office")}
              disabled={loader}
              type="text"
              error={!checkField("mwst_office", form)}
              value={form.mwst_office}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, mwst_office: e.target.value };
                })
              }
            />
          </div>
          <div className="col-span-4 md:col-span-6">
            <Label error={!checkField("mwst_number", form)} htmlFor={"mwstnumber"}>
              {t("mwst_number")}
            </Label>
            <Input
              id={"mwstnumber"}
              disabled={loader}
              error={!checkField("mwst_number", form)}
              value={form.mwst_number}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, mwst_number: e.target.value };
                })
              }
            />
          </div>

          <div className="col-span-4">
            <Autocomplete
              label={t("country")}
              className="h-10 w-full"
              required={!checkField("country", form)}
              value={countries.find((item) => item.id.toString() === form.country)}
              onChange={(newValue) => {
                if (!newValue) return;
                fetchCity(newValue.name);
                setForm((prev) => {
                  return {
                    ...prev,
                    country: newValue.id.toString(),
                  };
                });
              }}
              disableClearable
              loader={loader}
              options={countries}
              getOptionLabel={(option) => option?.name}
              getOptionValue={(option) => option?.id.toString()}
            />
          </div>
          <div className="col-span-4">
            <CityAutoComplete
              value={Number(form.city)}
              cities={cities}
              required={!checkField("city", form)}
              onSelect={(value) => {
                setForm((prev) => {
                  return {
                    ...prev,
                    city: value.value.toString(),
                  };
                });
              }}
            />
          </div>
          <div className="col-span-4">
            <Label error={!checkField("postal_code", form)} htmlFor={t("postal_code")}>
              {t("postal_code")}
            </Label>
            <Input
              id={t("postal_code")}
              disabled={loader}
              type="text"
              error={!checkField("postal_code", form)}
              value={form.postal_code}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, postal_code: e.target.value };
                })
              }
            />
          </div>
          <div className="col-span-4 sm:col-span-8 md:col-span-12">
            <Label error={!checkField("address_1", form)} htmlFor={t("address_1")}>
              {t("address_1")}
            </Label>
            <Input
              id={t("address_1")}
              disabled={loader}
              type="text"
              error={!checkField("address_1", form)}
              value={form.address_1}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, address_1: e.target.value };
                })
              }
            />
          </div>
          <div className="col-span-4 sm:col-span-8 md:col-span-12">
            <Label error={!checkField("address_2", form)} htmlFor={t("address_2")}>
              {t("address_2")}
            </Label>
            <Input
              id={t("address_2")}
              disabled={loader}
              type="text"
              error={!checkField("address_2", form)}
              value={form.address_2}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, address_2: e.target.value };
                })
              }
            />
          </div>
          <div className="col-span-4 md:col-span-6">
            <Label error={!checkField("tel1_title", form)} htmlFor={t("tel1_title")}>
              {t("tel1_title")}
            </Label>
            <Input
              id={t("tel1_title")}
              disabled={loader}
              type="text"
              error={!checkField("tel1_title", form)}
              value={form.tel1_title}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, tel1_title: e.target.value };
                })
              }
            />
          </div>
          <div className="col-span-4 md:col-span-6">
            <Label error={!checkField("tel1", form)} htmlFor={t("tel1")}>
              {t("tel1")}
            </Label>
            <Input
              id={t("tel1")}
              disabled={loader}
              type="text"
              error={!checkField("tel1", form)}
              value={form.tel1}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, tel1: e.target.value };
                })
              }
            />
          </div>
          <div className="col-span-4 md:col-span-6">
            <Label error={!checkField("email1_title", form)} htmlFor={t("email1_title")}>
              {t("email1_title")}
            </Label>
            <Input
              id={t("email1_title")}
              disabled={loader}
              type="text"
              error={!checkField("email1_title", form)}
              value={form.email1_title}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, email1_title: e.target.value };
                })
              }
            />
          </div>
          <div className="col-span-4 md:col-span-6">
            <Label error={!checkField("email1", form)} htmlFor={t("email1")}>
              {t("email1")}
            </Label>
            <Input
              id={t("email1")}
              disabled={loader}
              type="text"
              error={!checkField("email1", form)}
              value={form.email1}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, email1: e.target.value };
                })
              }
            />
          </div>
          <div className="col-span-4 sm:col-span-8 md:col-span-12">
            <Label error={!checkField("note", form)} htmlFor={t("note")}>
              {t("note")}
            </Label>
            <Input
              id={t("note")}
              disabled={loader}
              type="text"
              error={!checkField("note", form)}
              value={form.note}
              onChange={(e) =>
                setForm((prev) => {
                  return { ...prev, note: e.target.value };
                })
              }
            />
          </div>
        </div>
      )}
    </AccordionContent>
  );
};

const FinalStage = ({
  isStoreCardReadonly,
  loader,
  form,
  setForm,
  isOpen,
}: {
  isStoreCardReadonly: boolean;
  loader: boolean;
  form: Form;
  setForm: Dispatch<SetStateAction<Form>>;
  isOpen: boolean;
}) => {
  const t = useTranslations("general");
  const { $get } = useApi();
  const { setBackdrop } = useBackdrop();
  const [openUser, setOpenUser] = useState(false);
  const siteUsersRef = useRef<SiteUser[]>([]);
  const [lookups, setLookUps] = useState<LookupList>({
    printerTypes: [],
    storeTypes: [],
    periodTypes: [],
  });
  const [representatives, setRepresentatives] = useState<User[]>([]);

  const getData = async (user?: { data: SiteUser }) => {
    setBackdrop(true);
    setOpenUser(false);
    await $get(`site-user?short=1&status=1`)
      .then((res) => {
        siteUsersRef.current = res.data;
        if (user) {
          const targetItem = res.data.find((item: SiteUser) => item.token === user.data.token);
          if (!targetItem) return;
          setForm((prev) => {
            return {
              ...prev,
              user_name: targetItem.name,
              user_token: targetItem.token,
            };
          });
        }
      })
      .finally(() => {
        setBackdrop(false);
        //this.$store.commit('setLoader', false)
      });
  };

  const getRepresentatives = async () => {
    await $get(`user?site=${getSiteToken()}&representative=1`).then((res) => {
      setRepresentatives(res.data);
    });
  };

  const fetchLookUps = async () => {
    await $get("lookup-items?sort=value&items=printerTypes,storeTypes,periodTypes").then((res) => {
      setLookUps((prev) => {
        return {
          ...prev,
          printerTypes: res.data.find((item: { slug: string }) => item.slug === "printerTypes").list.item,
          storeTypes: res.data.find((item: { slug: string }) => item.slug === "storeTypes").list.item,
          periodTypes: res.data.find((item: { slug: string }) => item.slug === "periodTypes").list.item,
        };
      });
    });
  };

  const lookupFind = (value: string, list: LookupItem[]) => list.find((item) => item.value.toString() === value);

  useEffect(() => {
    if (isOpen && lookups.storeTypes.length === 0) {
      fetchLookUps();
    }
    if (isOpen && siteUsersRef.current.length === 0) {
      getData();
    }
    if (isOpen && representatives.length === 0) {
      getRepresentatives();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <AccordionContent className="max-h-[40dvh] overflow-y-auto p-4 border-b border-x rounded-md">
        <div className="grid grid-cols-4 sm:grid-cols-8 md:grid-cols-12 gap-2">
          <div className="col-span-full flex flex-row items-end gap-4">
            <div>
              <Autocomplete
                label={t("user_name")}
                value={form.user_token ? ({ token: form.user_token, name: form.user_name } as unknown as SiteUser) : null}
                onChange={(newValue) => {
                  setForm((prev) => {
                    return {
                      ...prev,
                      user_name: newValue?.name ?? "",
                      user_token: newValue?.token ?? "",
                    };
                  });
                }}
                required={!checkField("user_token", form) || !checkField("user_name", form)}
                loader={loader}
                options={siteUsersRef.current}
                getOptionLabel={(option) => option?.name}
                getOptionValue={(option) => option?.token}
              />
            </div>
            <div className="col-span-4 flex justify-end items-end pb-2">Or</div>
            <div className="col-span-4 md:col-span-4 flex justify-end items-end">
              <Button variant="success" onClick={() => setOpenUser(true)} loading={loader}>
                <Plus className="w-4 h-4 mr-2" />
                {t("newUser")}
              </Button>
            </div>
          </div>

          <div className="col-span-4">
            <Autocomplete
              className="w-full"
              label={t("inv_period")}
              value={lookupFind(form.inv_period?.toString() ?? "", lookups.periodTypes)}
              onChange={(newValue) => {
                if (!newValue) return;
                setForm((prev) => {
                  return {
                    ...prev,
                    inv_period: Number(newValue.value),
                  };
                });
              }}
              required={!checkField("inv_period", form)}
              disableClearable
              options={lookups.periodTypes}
              loader={loader}
              getOptionLabel={(option) => option?.label}
              getOptionValue={(option) => option?.value.toString()}
            />
          </div>
          <div className="col-span-4">
            <Autocomplete
              className="w-full"
              label={t("printer")}
              value={lookupFind(form.printer?.toString() ?? "", lookups.printerTypes)}
              onChange={(newValue) => {
                if (!newValue) return;
                setForm((prev) => {
                  return {
                    ...prev,
                    printer: Number(newValue.value),
                  };
                });
              }}
              required={!checkField("printer", form)}
              disableClearable
              loader={loader}
              options={lookups.printerTypes}
              getOptionLabel={(option) => option?.label}
              getOptionValue={(option) => option?.value.toString()}
            />
          </div>
          <div className="col-span-4">
            <Autocomplete
              className="w-full"
              label={t("type")}
              value={lookupFind(form.type?.toString() ?? "", lookups.storeTypes)}
              onChange={(newValue) => {
                if (!newValue) return;
                setForm((prev) => {
                  return {
                    ...prev,
                    type: Number(newValue.value),
                  };
                });
              }}
              required={!checkField("type", form)}
              loader={loader}
              disableClearable
              options={lookups.storeTypes}
              getOptionLabel={(option) => option?.label}
              getOptionValue={(option) => option?.value.toString()}
            />
          </div>
          <div className="col-span-4">
            <Autocomplete
              label={t("representative") + "(Optional)"}
              value={representatives.find((item) => item.token === form.representative)}
              onChange={(newValue) => {
                setForm((prev) => ({ ...prev, representative: newValue?.token ?? "" }));
              }}
              required={!checkField("representative", form)}
              loader={loader}
              options={representatives}
              getOptionLabel={(option) => option?.name}
              getOptionValue={(option) => option?.token}
              className="w-full"
            />
          </div>
          <Alert className="col-span-full" variant={"info"}>
            <AlertCircleIcon />
            <AlertTitle>Dikkat</AlertTitle>
            <AlertDescription>Bu müşterinin marka yöneticisini lütfen Store Representative ekranından da işaretleyin</AlertDescription>
          </Alert>
        </div>
      </AccordionContent>
      <CreateUser open={openUser} no={() => setOpenUser(false)} yes={(props) => getData(props)} />
    </>
  );
};

export const CityAutoComplete = ({ id, value, cities, onSelect, required }: { id?: string; value: number | undefined; cities: City[]; onSelect: (value: City) => void; required: boolean }) => {
  const t = useTranslations("general");
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const cityList = useMemo(
    () => cities.filter((item) => (inputValue.length > 0 ? item.name.toString().toLocaleLowerCase().includes(inputValue.toLocaleLowerCase()) : true)).slice(0, 50),
    [cities, inputValue]
  );

  const findCity = (value: number | undefined) => {
    return cityList.find((city) => city.value.toString() === value?.toString())?.name;
  };

  return (
    <>
      <Label>{t("city")}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button id={id} variant="outline" className={cn("w-full justify-between h-10", required ? "text-destructive border-destructive" : "", !findCity(value) && "text-muted-foreground")}>
            {findCity(value) ?? "z.B. Berlin"}
            <ChevronDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0">
          <Command shouldFilter={false}>
            <CommandInput className="h-9" onValueChange={setInputValue} />
            <CommandList>
              <CommandEmpty>Nicht gefunden</CommandEmpty>
              <CommandGroup>
                {cityList.map((card) => (
                  <CommandItem
                    key={card.id}
                    value={card.id.toString()}
                    onSelect={() => {
                      onSelect(card);
                      setOpen(false);
                    }}
                  >
                    {card.name}
                    <Check className={cn("ml-auto", value === card.id ? "opacity-100" : "opacity-0")} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default ParietteCreateModal;
